'use client';

import { useCallback, useEffect, useState } from 'react';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAppShell } from '@/providers/app-shell-provider';
import { AppDrawer } from './AppDrawer';
import { ShellLogo } from './ShellLogo';
import { ShellTooltip } from './ShellTooltip';
import { SidebarContents } from './SidebarContents';
import { SHELL_FOCUS } from './SidebarNav';

export function AppSidebar() {
  const { sidebarCollapsed, toggleSidebar } = useAppShell();
  const { t } = useTranslation();
  const [overlayOpen, setOverlayOpen] = useState(false);
  const toggle = useCallback(() => {
    if (window.matchMedia('(min-width: 1280px)').matches) toggleSidebar();
    else setOverlayOpen((open) => !open);
  }, [toggleSidebar]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.closest('input, textarea, select') || target.isContentEditable)
      )
        return;
      if (
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        event.key.toLowerCase() === 'b'
      ) {
        // The sidebar is CSS-hidden below lg; shortcuts must not open a second
        // drawer on mobile. offsetParent follows the same CSS visibility.
        if (
          !document.querySelector<HTMLElement>('[data-app-sidebar]')
            ?.offsetParent
        )
          return;
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);
  const header = (rail: boolean) => {
    const label = t(
      rail ? 'navigation.expandSidebar' : 'navigation.collapseSidebar',
    );
    const Icon = rail ? PanelLeftOpen : PanelLeftClose;
    return (
      <div
        className={cn(
          'flex items-center border-b border-[var(--fd-line)]',
          rail ? 'flex-col gap-1 py-2' : 'h-16 justify-between px-3',
        )}
      >
        <ShellLogo rail={rail} />
        <ShellTooltip label={<>{label} · Ctrl/⌘+B</>}>
          <button
            type="button"
            onClick={toggle}
            aria-label={label}
            aria-keyshortcuts="Control+B Meta+B"
            className={cn(
              'flex size-10 shrink-0 items-center justify-center rounded-md text-[var(--fd-muted)] hover:bg-[var(--fd-bg)] hover:text-[var(--fd-ink)]',
              SHELL_FOCUS,
            )}
          >
            <Icon className="size-[18px]" aria-hidden />
          </button>
        </ShellTooltip>
      </div>
    );
  };
  return (
    <>
      <aside
        data-app-sidebar
        className={cn(
          'fd-header sticky top-0 hidden h-dvh w-16 shrink-0 overflow-y-auto border-r border-[var(--fd-line)] lg:block',
          !sidebarCollapsed && 'xl:w-64',
        )}
      >
        <div className={cn('h-full', !sidebarCollapsed && 'xl:hidden')}>
          <SidebarContents rail header={header(true)} />
        </div>
        {!sidebarCollapsed && (
          <div className="hidden h-full xl:block">
            <SidebarContents header={header(false)} />
          </div>
        )}
      </aside>
      <AppDrawer open={overlayOpen} onOpenChange={setOverlayOpen} desktop />
    </>
  );
}
